/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { ConfigurationService } from 'src/app/config/configuration.service';
import { PoiDetail } from '../model/poidetail/poidetail';
import { PoiListItem, PoiListItemType, PoiOrAggregate } from '../model/poibase';

const poiTypeMap = {
  '001PST': { marker: 'poi-branch', icon: 1029 },
  '001MP24': { marker: 'poi-mypost24', icon: 1006 },
  '001AG-PICK': { marker: 'poi-pickpost', icon: 1011 },
  '003BE': { marker: 'poi-letterbox', icon: 2166 },
  '004PSTMAT': { marker: 'poi-atm', icon: 3160 },
  '0017': { marker: 'poi-postofficebox', icon: 1030 },
  '001HS': { marker: 'poi-homeservice', icon: 1012 },
  '001PFFIL': { marker: 'poi-pf', icon: 10001 },
  '001BZ': { marker: 'poi-center-letters', icon: 2164 },
  '001BZ-INTERN': { marker: 'poi-center-letters', icon: 2164 },
  '001LZB': { marker: 'poi-logistic-center-letters', icon: 2162 },
  '001LZB-INTERN': { marker: 'poi-logistic-center-letters', icon: 2162 },
  '001LZP': { marker: 'poi-logistic-center-parcel', icon: 2163 },
  '001LZP-INTERN': { marker: 'poi-logistic-center-parcel', icon: 2163 },
  '001PZ': { marker: 'poi-center-parcel', icon: 10000 },
  '001PZ-INTERN': { marker: 'poi-center-parcel', icon: 10000 },
  '001AL': { marker: 'poi-ap-logistics', icon: 10002 },
  '001GLS': { marker: 'poi-ap-logistics', icon: 10002 },
  '001TKST': { marker: 'poi-gas', icon: 3122 },
  '001PFST': { marker: 'poi-postofficebox', icon: 1030 },
  '001GKSS': { marker: 'poi-gk-self-service', icon: 2290 },
  '001VPA': { marker: 'poi-postbus-sales-office', icon: 3107 },
  '001PCC': { marker: 'poi-company-cars', icon: 3117 },
  '001PCC-OC': { marker: 'poi-company-cars-oc', icon: 3111 },
};

const defaultTypeMapEntry = { marker: 'poi', icon: 1029 };

@Injectable({
  providedIn: 'root',
})
export class MapService {
  constructor(
    private readonly configurationService: ConfigurationService,
    private logger: NGXLogger
  ) {}

  public markerUrl(item: PoiOrAggregate | PoiDetail, cluster = false, active = false): string {
    let marker = null;

    if (cluster) {
      marker = `poi-cluster`;
    } else if (item instanceof PoiDetail) {
      const poiTypeId = item.typeId || this.extractPoiTypeId(item.id);
      marker = poiTypeMap[poiTypeId] ? poiTypeMap[poiTypeId].marker : defaultTypeMapEntry.marker;
    } else if (item.type === PoiListItemType.poi) {
      const poiTypeId = (item as PoiListItem).serviceType?.id || this.extractPoiTypeId((item as PoiListItem).id);
      const mappedPoi = poiTypeMap[poiTypeId];
      marker = mappedPoi ? mappedPoi.marker : defaultTypeMapEntry.marker;
    } else if (item.type === PoiListItemType.aggregate) {
      marker = `aggregate`;
    }

    if (!marker) {
      const fallbackPoiType = (item as PoiListItem)?.serviceType?.id || this.extractPoiTypeId((item as PoiListItem)?.id);
      this.logger.warn(fallbackPoiType, ' has no marker defined');
      marker = `poi`;
    }

    return `${this.configurationService.getConfiguration().webBaseUrl}/assets/map/markers/${marker}${active ? '-active' : ''}.png`;
  }

  public currentLocationUrl() {
    return `${this.configurationService.getConfiguration().webBaseUrl}/assets/map/markers/current-location.png`;
  }

  public iconUrl(item: PoiListItem) {
    const poiTypeId = item?.serviceType?.id || this.extractPoiTypeId(item?.id);
    if (poiTypeId in poiTypeMap) {
      return poiTypeMap[poiTypeId].icon;
    }
    return defaultTypeMapEntry.icon;
  }

  private extractPoiTypeId(poiId?: string): string {
    if (!poiId) {
      return '';
    }
    const separatorIndex = poiId.indexOf('_');
    return separatorIndex > -1 ? poiId.substring(0, separatorIndex) : poiId;
  }
}
